import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketDataService } from "../Context/WebsocketContext";
import { getConferenceCallDetailsApiCall } from "../API/conferences/conferencesService";
import { handleReceivedMessages } from "../Helpers/chatHelper/ChatHelper";
import {
  DISABLED_TRANSLATION,
  REAL_TIME_TRANSLATION,
  SEQUENCIAL_TRANSLATION,
} from "../Constants/constantValues";

const removeDuplicateMessageForMessage = (list, message) =>
  list.filter(
    (item) =>
      !(
        item.original_message === message.original_message &&
        item.user_id === message.user_id
      )
  );

export const useConferenceAttendeeSession = ({
  conferenceId,
  callUuid,
  languageKey,
  joinMode = "online",
}) => {
  const {
    add_observer,
    remove_observer,
    updateConferenceChannelSubscription,
    clearConferenceChannelSubscription,
    sendConferenceLanguageUpdate,
  } = useWebSocketDataService();

  const [callAccess, setCallAccess] = useState(null);
  const [speechRecognitionMessages, setSpeechRecognitionMessages] = useState([]);
  const [callEnded, setCallEnded] = useState(false);
  const messagesRef = useRef([]);
  const translationActiveRef = useRef(false);
  const [translationActive, setTranslationActiveState] = useState(false);

  const setTranslationActive = (value) => {
    translationActiveRef.current = value;
    setTranslationActiveState(value);
  };

  useEffect(() => {
    if (!conferenceId) return;

    getConferenceCallDetailsApiCall(
      conferenceId,
      (response) => {
        const access = response?.data?.access;
        if (access) setCallAccess(access);

        const initialType = response?.data?.details?.translation_type;
        setTranslationActive(!!initialType && initialType !== DISABLED_TRANSLATION);
      },
      () => {}
    );
  }, [conferenceId]);

  useEffect(() => {
    if (!callUuid || !languageKey) return;

    updateConferenceChannelSubscription(callUuid, languageKey, joinMode);

    return () => {
      clearConferenceChannelSubscription(callUuid, languageKey, joinMode);
    };
  }, [
    callUuid,
    languageKey,
    joinMode,
    updateConferenceChannelSubscription,
    clearConferenceChannelSubscription,
  ]);

  useEffect(() => {
    if (!callUuid) return;

    const observer = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const msg = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
          if (msg.call_uuid !== callUuid) return;

          if (msg.update === "close") {
            setCallEnded(true);
            return;
          }

          if (msg.message_type === "language_updated") {
            if (!msg.language) return;
            setCallAccess((prev) => {
              const base = prev || { streams: [] };
              const streams = base.streams ?? [];
              const hasStream = streams.some((s) => s.language_key === msg.language);
              const nextStreams = hasStream
                ? streams.map((s) =>
                    s.language_key === msg.language
                      ? {
                          ...s,
                          playback_url: msg.tts_playback_url ?? s.playback_url,
                          playback_id: msg.tts_playback_id ?? s.playback_id,
                        }
                      : s
                  )
                : [
                    ...streams,
                    {
                      language_key: msg.language,
                      playback_url: msg.tts_playback_url ?? null,
                      playback_id: msg.tts_playback_id ?? null,
                    },
                  ];
              return { ...base, streams: nextStreams };
            });
            return;
          }

          if (
            msg.enabled &&
            (msg.translation_type === REAL_TIME_TRANSLATION ||
              msg.translation_type === SEQUENCIAL_TRANSLATION)
          ) {
            setTranslationActive(true);
            return;
          }

          if (msg.enabled === false && msg.translation_type === null) {
            setTranslationActive(false);
            return;
          }

          if (!msg.original_message || !translationActiveRef.current) return;

          messagesRef.current = removeDuplicateMessageForMessage(messagesRef.current, msg);
          const merged = handleReceivedMessages(messagesRef.current, msg);
          const sorted = merged.sort((a, b) =>
            a.device_sent_date < b.device_sent_date ? 1 : -1
          );
          messagesRef.current = sorted;
          setSpeechRecognitionMessages(sorted);
        } catch (_) {}
      },
    };

    add_observer(observer);
    return () => remove_observer(observer);
  }, [callUuid, add_observer, remove_observer]);

  const requestLanguageSwitch = useCallback(
    (newLanguageKey) => {
      if (!callUuid || !newLanguageKey || newLanguageKey === languageKey) return;
      sendConferenceLanguageUpdate(callUuid, newLanguageKey);
    },
    [callUuid, languageKey, sendConferenceLanguageUpdate]
  );

  return { callAccess, speechRecognitionMessages, requestLanguageSwitch, callEnded, translationActive };
};
